<template>
  <div class="flex items-center gap-4 mb-4">
    <div class="grid grid-cols-1 w-full">
      <label
        v-if="!isSubmitting"
        :for="uniqueId"
        class="w-full p-5 text-center border-2 border-dashed rounded-lg cursor-pointer border-gray-80"
        :class="{ 'bg-blue-50': isDragging }"
        @dragover.prevent="onDragOver"
        @dragleave="onDragLeave"
        @drop.prevent="onDrop"
      >
        <div class="flex items-center justify-center ">
          <div class="grid grid-cols-1">
            <div>
              <Icon name="solar:upload-minimalistic-bold-duotone" />
            </div>
            <div class="mb-2 text-sm">
              <span class="font-semibold">
                Drag and drop your files here or browse
              </span>
            </div>
          </div>
        </div>
        <input
          :id="uniqueId"
          type="file"
          class="hidden"
          :accept="fileType"
          :multiple="multipleFilesUpload"
          @change="handleFileChange"
        >
      </label>

      <div
        v-else
        class="w-full p-5 text-center border-2 border-dashed rounded-lg border-gray-80"
      >
        <div class="flex items-center justify-center ">
          <div class="grid grid-cols-1">
            <div class="text-sm">
              <span class="font-semibold">
                Submitting...
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="selectedFiles && selectedFiles.length > 0"
        class="grid grid-cols-1 gap-y-1 divide-y border rounded mt-2"
      >
        <div
          v-for="(file, index) in selectedFiles"
          :key="index"
          class="flex items-center gap-2 p-2"
        >
          <div class="w-full flex items-center justify-between gap-3">
            <div>
              {{ file.name }}
            </div>

            <div class="flex shrink-0">
              <Button
                text
                severity="danger"
                icon="pi pi-trash"
                size="small"
                @click="removeFile(index)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const { uploadToPublicStorage } = useStorage()

const emit = defineEmits(['bindValue'])

// STATE
const toast = useToast()
const isDragging = ref(false)

const props = defineProps({
  uniqueId: {
    type: String,
    required: false,
    default: 'dropzone-file',
  },
  maxFileSizeMb: {
    type: Number,
    required: false,
    default: 0,
  },
})

const fileType = defineModel<string>('fileType', {
  type: String,
  required: false,
  default: 'allowed',
})

const selectedFiles = defineModel<Array<object>>('selectedFiles', {
  type: Array,
  required: false,
  default: () => [],
})

const isSubmitting = defineModel<boolean>('isSubmitting', {
  type: Boolean,
  required: false,
  default: false,
})

const multipleFilesUpload = defineModel<boolean>('multipleFilesUpload', {
  type: Boolean,
  required: false,
  default: true,
})

const filesPath = defineModel<string>('filesPath', {
  type: String,
  required: false,
  default: '',
})

const uploadedFiles = defineModel<Array<object>>('uploadedFiles', {
  type: Array,
  required: false,
  default: () => [],
})

// ACTIONS
const onDragOver = () => {
  isDragging.value = true
}

const onDragLeave = () => {
  isDragging.value = false
}

const onDrop = (event) => {
  isDragging.value = false
  handleFileChangeFromDnD(event.dataTransfer.files)
}

const withinSizeLimit = (files: File[]): File[] => {
  if (!props.maxFileSizeMb || props.maxFileSizeMb <= 0) return files

  const maxBytes = props.maxFileSizeMb * 1024 * 1024
  const oversized = files.filter(file => file.size >= maxBytes)
  if (oversized.length > 0) {
    toast.add({
      severity: 'warn',
      summary: 'File too large',
      detail: `Max file size is ${props.maxFileSizeMb}MB. Skipped: ${oversized.map(file => file.name).join(', ')}`,
      life: 4000,
    })
  }

  return files.filter(file => file.size < maxBytes)
}

const handleFileChangeFromDnD = (files) => {
  if (multipleFilesUpload.value == false && selectedFiles.value.length >= 1) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'You can only upload 1 file', life: 3000 })
    return
  }

  if (files) {
    selectedFiles.value = selectedFiles.value.concat(withinSizeLimit(Array.from(files)))
  }
}

const handleFileChange = (event) => {
  if (multipleFilesUpload.value == false && selectedFiles.value.length >= 1) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'You can only upload 1 file', life: 3000 })
    return
  }

  const input = event.target as HTMLInputElement
  if (input.files) {
    selectedFiles.value = selectedFiles.value.concat(withinSizeLimit(Array.from(input.files)))
  }
}

const uploadFiles = async () => {
  isSubmitting.value = true

  try {
    const results = []
    for (const selectedFile of selectedFiles.value) {
      if (!selectedFile.publicUrl) {
        const res = await uploadToPublicStorage({ file: selectedFile, prefix: filesPath.value }) as { data?: { url?: string, storagePath?: string } }

        results.push({
          name: selectedFile.name,
          publicUrl: res?.data?.url,
          // dropboxFileId is reused as the attachment's unique key (consumers remove/edit by it).
          dropboxFileId: res?.data?.storagePath,
          dropboxFileType: selectedFile.type,
          dropboxFileSize: selectedFile.size,
          uploadedAt: new Date(),
        })
      }
      else {
        // if the selected file has a key of publicUrl, it means it is already uploaded
        results.push(selectedFile)
      }
    }

    uploadedFiles.value = results
    selectedFiles.value = []

    emit('bindValue', results)
  }
  catch (error: unknown) {
    const err = error as { data?: { statusMessage?: string }, message?: string }
    toast.add({
      severity: 'error',
      summary: 'Upload failed',
      detail: err?.data?.statusMessage || err?.message || 'Something went wrong',
      life: 4000,
    })
    throw error
  }
  finally {
    isSubmitting.value = false
  }
}

const removeFile = (index) => {
  const newFiles = [...selectedFiles.value]
  newFiles.splice(index, 1)
  selectedFiles.value = newFiles
}

defineExpose({
  uploadFiles,
  selectedFiles,
})
</script>
